import { Component, Input, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { FormControl } from '@angular/forms';

import { LocaleService } from '@next/core-lib/i18n';
import { SiteState } from '@next/core-lib/site';
import { TableComponent } from '@next/core-lib/table-v2';
import { Store, select } from '@ngrx/store';

import { Observable, Subject, debounceTime, distinctUntilChanged, map, takeUntil } from 'rxjs';

import { TreatmentTypes } from 'src/app/overview/models';

import { helpLegendAnimations } from './help-legend.animations';
import { deleteOrderLine, deleteOrderLines, editOrderLine, saveCustomerProductInventory, saveOrderLine } from '../../../store/actions';
import * as OverviewSelectors from '../../../store/selectors';
import { OrderPlanOverviewDataSource } from '../../datasource/orderplan.datasource';
import {
  CropAid,
  DeliveryPeriodMomentType,
  OrderLine,
  OrderLineStatus,
  OrderPlanOverviewV2,
  Package,
  ProductInventoryData,
  SalesOrderPeriod,
  StockEditModel,
  TableColumnsInfo,
} from '../../models';

@Component({
  selector: 'app-order-planning-table',
  templateUrl: './order-planning-table.component.html',
  styleUrls: ['./order-planning-table.component.scss'],
  animations: helpLegendAnimations,
  standalone: false,
})
export class OrderPlanningTableComponent implements OnInit, OnDestroy {
  private readonly store = inject<Store<SiteState>>(Store);
  readonly locale = inject(LocaleService);

  @ViewChild(TableComponent) tableComponent: TableComponent<OrderPlanOverviewV2>;

  @Input() columnsToDisplay: string[] = [];
  @Input() dynamicPeriodsColumns: TableColumnsInfo[] = [];

  salesOrderPeriods: SalesOrderPeriod[];
  cropAids: CropAid[];
  packages: Package[];
  isEmpty$: Observable<boolean>;
  productInventoryChanged: Subject<ProductInventoryData> = new Subject<ProductInventoryData>();
  inventories: ProductInventoryData[];
  selectedRows: OrderPlanOverviewV2[] = [];
  helpVisibleState = 'inactive';
  readonly dataSource: OrderPlanOverviewDataSource;
  private inventoryChanged$ = new Subject<StockEditModel>();

  private readonly destroyed$ = new Subject<boolean>();

  constructor() {
    this.dataSource = new OrderPlanOverviewDataSource(this.store);
  }

  ngOnInit(): void {
    this.getSalesOrderPeriods();
    this.getCropAids();
    this.getPackages();
    this.isEmpty$ = this.dataSource.data$.pipe(map((data) => data.length === 0));
    this.getOrderplanByPeriodsDisplayData();
    this.onProductInventoryChanged();
    this.subscribeToInventoryChange();
  }

  onProductInventoryChanged(): void {
    this.productInventoryChanged
      .pipe(
        debounceTime(1000),
        distinctUntilChanged(),
        takeUntil(this.destroyed$),
        map((inventory: ProductInventoryData) => {
          const amount = Math.round(+inventory.amount * 100) / 100;
          return { ...inventory, amount };
        }),
      )
      .subscribe((inventory) => {
        this.saveCropAidInventory(inventory);
      });
  }

  isCropAidActive(orderline: OrderPlanOverviewV2): boolean {
    const cropAid = this.cropAids.find((c) => c.id === orderline.cropAidId);
    return cropAid?.isActive === false;
  }

  orderLineHasMoreThanOnePackage(orderline: OrderPlanOverviewV2): boolean {
    const orderlineProductId = this.getCropAid(orderline.cropAidId)?.productId;
    return !orderline.isPackage &&
      this.dataSource.data$.value?.find(
        (data) => this.getCropAid(data.cropAidId)?.productId === orderlineProductId && this.getAvailablePackages(data).length > 1,
      )
      ? true
      : false;
  }

  getOrderPlanPeriodByPeriodName(row: OrderPlanOverviewV2, periodName: string) {
    return row.orderplanPeriods.find((orderplanPeriod) => orderplanPeriod.periodName === periodName);
  }

  ngOnDestroy() {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  cropAidInventoryChanged(amount: string, cropAidId: number, row?: OrderPlanOverviewV2, inputControl?: FormControl) {
    if (amount === null || amount === undefined) {
      return;
    }

    const numericAmount = Number(amount);
    if (numericAmount < 0) {
      if (row) {
        inputControl?.setValue(row.customerProductInventoryAmount);
      }
      return;
    }

    if (row && amount !== '') {
      row.customerProductInventoryAmount = numericAmount;
    }
    this.inventoryChanged$.next({ amount, cropAidId });
  }

  subscribeToInventoryChange(): void {
    this.inventoryChanged$.pipe(debounceTime(500)).subscribe((value: StockEditModel) => {
      if (value.amount !== '') {
        this.productInventoryChanged.next({ amount: Number(value.amount), cropAidId: value.cropAidId });
      }
    });
  }

  getOrderplanByPeriodsDisplayData(): void {
    this.store.pipe(select(OverviewSelectors.selectOrderplanV2Display), takeUntil(this.destroyed$)).subscribe((result) => {
      if (result) {
        this.inventories = [];
        result.forEach((orderPlan) => {
          if (!this.inventories.some((x) => x.cropAidId === orderPlan.cropAidId)) {
            this.inventories.push({
              amount: orderPlan.customerProductInventoryAmount,
              cropAidId: orderPlan.cropAidId,
            });
          }
        });
        this.selectedRows = [];
      }
    });
  }

  selectionChanged(data: OrderPlanOverviewV2[]): void {
    this.selectedRows = data;
  }

  deleteOrderLines(): void {
    const ids = this.selectedRows
      .map((row) =>
        row.orderplanPeriods
          .map((op) => op.orderLines.filter((ol) => ol.status === OrderLineStatus.open).map((ol) => ol.id))
          .reduce((ols, ol) => ols.concat(ol), [] as number[]),
      )
      .reduce((allOrderplanPeriods, orderplanPeriod) => allOrderplanPeriods.concat(orderplanPeriod), [] as number[]);

    if (ids.length) {
      this.store.dispatch(deleteOrderLines({ ids }));
    }

    this.tableComponent.clearSelections();
  }

  editOrDeleteOrderLine(orderLine: OrderLine, cropAidId: number) {
    if (!orderLine.count || orderLine.count === 0) {
      if (orderLine.id !== -1) {
        this.store.dispatch(deleteOrderLine({ id: orderLine.id, noDataRefreshNeeded: false }));
      }
    } else {
      this.store.dispatch(saveOrderLine({ orderLine, cropAidId }));
    }
  }

  addOrderLine(cropAidId: number = null, periodId: number = null) {
    this.store.dispatch(editOrderLine({ orderLine: this.getEmptyOrderLine(periodId), cropAidId }));
  }

  allPackagesShown = (orderLine: OrderPlanOverviewV2) => this.getGroupedPackageCount(orderLine.cropAidName) === this.getNumberOfPackages(orderLine);

  toggleHelp() {
    this.helpVisibleState = this.helpVisibleState === 'active' ? 'inactive' : 'active';
  }

  private getGroupedPackageCount = (cropAidName: string) =>
    this.dataSource.data$.value?.filter((row) => row.isPackage && row.cropAidName === cropAidName).length ?? 0;

  private getCropAids() {
    this.store.pipe(takeUntil(this.destroyed$), select(OverviewSelectors.selectCropAids)).subscribe((result) => (this.cropAids = result));
  }

  private getSalesOrderPeriods() {
    this.store.pipe(select(OverviewSelectors.selectSalesOrderPeriods), takeUntil(this.destroyed$)).subscribe((periods) => {
      this.salesOrderPeriods = periods;
    });
  }

  private getPackages() {
    this.store.pipe(takeUntil(this.destroyed$), select(OverviewSelectors.selectPackages)).subscribe((result) => (this.packages = result));
  }

  private getCropAid(cropAidId: number) {
    return this.cropAids?.find(({ id }) => id === cropAidId);
  }

  private getAvailablePackages(orderline: OrderPlanOverviewV2) {
    if (!orderline.packageId) {
      return [];
    }
    const productId = this.getCropAid(orderline.cropAidId)?.productId;
    if (!productId) {
      return [];
    }

    return this.packages.filter((pack) => pack.productId === productId && pack.isActive);
  }

  private saveCropAidInventory(data: ProductInventoryData) {
    if (data.amount < 0) {
      return;
    }

    const currentInventory = this.inventories.find((x) => x.cropAidId === data.cropAidId);
    currentInventory.amount = data.amount;
    this.store.dispatch(saveCustomerProductInventory({ productInventories: this.inventories }));
  }

  private getNumberOfPackages(orderPlanItem: OrderPlanOverviewV2) {
    const orderlineProductId = this.getCropAid(orderPlanItem.cropAidId)?.productId;
    const parentOrderLine = this.dataSource.data$.value?.find(
      (data) => this.getCropAid(data.cropAidId)?.productId === orderlineProductId && this.getAvailablePackages(data).length > 1,
    );
    return this.getAvailablePackages(parentOrderLine).length;
  }

  private getEmptyOrderLine(periodId: number): OrderLine {
    return {
      id: -1,
      packageId: null,
      count: null,
      year: null,
      periodId,
      status: OrderLineStatus.open,
      deliveryPeriodMoment: DeliveryPeriodMomentType.empty,
      treatmentType: TreatmentTypes.GBM,
      orderedDate: null,
      shipToId: 1,
      addedByOptimizer: false,
      cartId: -1,
      orderedId: -1,
      checkoutId: -1,
    };
  }
}
